import React, { useState } from "react";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import Button from "@components/Button";
import Loading from "@components/Loading";
import styled from "@emotion/styled";
import { attachUserDevice } from "@services/adminService";
import { toast } from "react-toastify";

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #494c5b;
`;

interface Props {
  visible: boolean;
  userId: string;
  onClose: () => void;
  onAttached?: () => void;
}

/**
 * Admin counterpart of the client "New device" dialog: attaches an existing
 * device (auto-created via MQTT) to the given user with the same fields the
 * user would fill in themselves — so it shows up in their app immediately.
 */
const AttachDeviceDialog: React.FC<Props> = ({
  visible,
  userId,
  onClose,
  onAttached,
}) => {
  const [esn, setEsn] = useState("");
  const [name, setName] = useState("");
  const [model, setModel] = useState("");
  const [width, setWidth] = useState("");
  const [saving, setSaving] = useState(false);

  const disabled = !esn.trim() || !name.trim() || !model.trim() || !width;

  const reset = () => {
    setEsn("");
    setName("");
    setModel("");
    setWidth("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAttach = async () => {
    if (disabled || saving) return;
    setSaving(true);
    try {
      await attachUserDevice(userId, {
        esn: esn.trim(),
        name: name.trim(),
        model: model.trim(),
        width: Number(width),
      });
      toast.success(`Device ${esn.trim()} attached`);
      reset();
      onAttached?.();
      onClose();
    } catch {
      // error toast is shown by api client
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      destroyOnClose
      visible={visible}
      onClose={handleClose}
      title="Attach device"
      style={{ width: 384 }}
    >
      <FieldWrap>
        <Label>Serial number (ESN)</Label>
        <Input
          value={esn}
          placeholder="e.g. 106590559"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEsn(e.target.value)
          }
        />
      </FieldWrap>
      <SizedBox height={16} />
      <FieldWrap>
        <Label>Harvester name</Label>
        <Input
          value={name}
          placeholder="e.g. Harvester #1"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
        />
      </FieldWrap>
      <SizedBox height={16} />
      <FieldWrap>
        <Label>Harvester model</Label>
        <Input
          value={model}
          placeholder="e.g. John Deere T670"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setModel(e.target.value)
          }
        />
      </FieldWrap>
      <SizedBox height={16} />
      <FieldWrap>
        <Label>Header width (meters)</Label>
        <Input
          type="number"
          value={width}
          placeholder="0"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setWidth(e.target.value)
          }
        />
      </FieldWrap>
      <SizedBox height={24} />
      <Row>
        <Button kind="outline" onClick={handleClose}>
          Cancel
        </Button>
        <SizedBox width={16} />
        <Button
          onClick={!saving ? handleAttach : undefined}
          disabled={saving || disabled}
        >
          {saving ? <Loading /> : "Attach"}
        </Button>
      </Row>
    </Dialog>
  );
};

export default AttachDeviceDialog;
